import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ServerIcon,
  QueueListIcon,
  UsersIcon,
  ChartBarIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  PaperAirplaneIcon,
  ArrowsPointingInIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  PlayIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

const KafkaStatus = () => {

  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [kafkaStatus, setKafkaStatus] = useState(null);
  const [clusterInfo, setClusterInfo] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [toast, setToast] = useState(null);

  // Topic management state
  const [showCreateTopic, setShowCreateTopic] = useState(false);
  const [newTopic, setNewTopic] = useState({ name: '', partitions: 3, replication_factor: 1 });
  const [topicDetail, setTopicDetail] = useState(null);
  const [topicMessages, setTopicMessages] = useState(null);
  const [showTopicDetail, setShowTopicDetail] = useState(false);
  const [showMessages, setShowMessages] = useState(false);

  // Consumer group state
  const [groupDetail, setGroupDetail] = useState(null);
  const [showGroupDetail, setShowGroupDetail] = useState(false);
  const [showResetOffsets, setShowResetOffsets] = useState(false);
  const [resetForm, setResetForm] = useState({ groupId: '', topic: '', to_earliest: true });

  // Produce message state
  const [showProduceModal, setShowProduceModal] = useState(false);
  const [produceForm, setProduceForm] = useState({ topic: '', key: '', value: '' });

  // Confirm delete state
  const [confirmDelete, setConfirmDelete] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchKafkaStatus = useCallback(async () => {
    try {
      const response = await api.get('/admin/kafka/status');
      setKafkaStatus(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to fetch Kafka status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClusterInfo = useCallback(async () => {
    try {
      const response = await api.get('/admin/kafka/cluster');
      setClusterInfo(response.data);
    } catch (err) { /* silent */ }
  }, []);

  const fetchServiceStatus = useCallback(async () => {
    try {
      const response = await api.get('/admin/kafka/services');
      setServiceStatus(response.data);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchKafkaStatus();
    fetchClusterInfo();
    fetchServiceStatus();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchKafkaStatus();
      fetchServiceStatus();
    }, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchKafkaStatus, fetchServiceStatus]);

  // Service Control
  const handleServiceControl = async (service, action) => {
    const key = `${service}-${action}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      const response = await api.post('/admin/kafka/service-control', { service, action });
      if (response.data.success) {
        showToast('success', response.data.message);
        setTimeout(() => { fetchKafkaStatus(); fetchServiceStatus(); fetchClusterInfo(); }, 3000);
      } else {
        showToast('error', response.data.message);
      }
    } catch (err) {
      showToast('error', err.response?.data?.error || err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Topic CRUD
  const handleCreateTopic = async () => {
    if (!newTopic.name.trim()) { showToast('error', 'Topic name is required'); return; }
    setActionLoading(prev => ({ ...prev, createTopic: true }));
    try {
      await api.post('/admin/kafka/topics', newTopic);
      showToast('success', `Topic '${newTopic.name}' created successfully`);
      setShowCreateTopic(false);
      setNewTopic({ name: '', partitions: 3, replication_factor: 1 });
      setTimeout(fetchKafkaStatus, 1000);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to create topic');
    } finally {
      setActionLoading(prev => ({ ...prev, createTopic: false }));
    }
  };

  const handleDeleteTopic = async (topicName) => {
    setActionLoading(prev => ({ ...prev, [`delete-${topicName}`]: true }));
    try {
      await api.delete(`/admin/kafka/topics/${topicName}`);
      showToast('success', `Topic '${topicName}' deleted`);
      setConfirmDelete(null);
      setTimeout(fetchKafkaStatus, 1000);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to delete topic');
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete-${topicName}`]: false }));
    }
  };

  const handlePurgeTopic = async (topicName) => {
    setActionLoading(prev => ({ ...prev, [`purge-${topicName}`]: true }));
    try {
      await api.post(`/admin/kafka/topics/${topicName}/purge`);
      showToast('success', `Topic '${topicName}' purged`);
      setConfirmDelete(null);
      setTimeout(fetchKafkaStatus, 2000);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to purge topic');
    } finally {
      setActionLoading(prev => ({ ...prev, [`purge-${topicName}`]: false }));
    }
  };

  const handleViewTopicDetail = async (topicName) => {
    try {
      const response = await api.get(`/admin/kafka/topics/${topicName}`);
      setTopicDetail(response.data);
      setShowTopicDetail(true);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to get topic detail');
    }
  };

  const handleViewMessages = async (topicName) => {
    try {
      const response = await api.get(`/admin/kafka/topics/${topicName}/messages?limit=20`);
      setTopicMessages({ topic: topicName, ...response.data });
      setShowMessages(true);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to read messages');
    }
  };

  // Produce message
  const handleProduceMessage = async () => {
    if (!produceForm.topic || !produceForm.value) { showToast('error', 'Topic and value are required'); return; }
    setActionLoading(prev => ({ ...prev, produce: true }));
    try {
      let value = produceForm.value;
      try { value = JSON.parse(value); } catch (e) { /* keep as string */ }
      await api.post('/admin/kafka/produce', {
        topic: produceForm.topic, key: produceForm.key, value
      });
      showToast('success', `Message produced to '${produceForm.topic}'`);
      setShowProduceModal(false);
      setProduceForm({ topic: '', key: '', value: '' });
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to produce message');
    } finally {
      setActionLoading(prev => ({ ...prev, produce: false }));
    }
  };

  // Consumer Group operations
  const handleViewGroupDetail = async (groupId) => {
    try {
      const response = await api.get(`/admin/kafka/consumer-groups/${groupId}`);
      setGroupDetail(response.data);
      setShowGroupDetail(true);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to get group detail');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    setActionLoading(prev => ({ ...prev, [`delgrp-${groupId}`]: true }));
    try {
      await api.delete(`/admin/kafka/consumer-groups/${groupId}`);
      showToast('success', `Consumer group '${groupId}' deleted`);
      setConfirmDelete(null);
      setTimeout(fetchKafkaStatus, 1000);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to delete group');
    } finally {
      setActionLoading(prev => ({ ...prev, [`delgrp-${groupId}`]: false }));
    }
  };

  const handleResetOffsets = async () => {
    if (!resetForm.groupId || !resetForm.topic) { showToast('error', 'Group and topic are required'); return; }
    setActionLoading(prev => ({ ...prev, resetOffsets: true }));
    try {
      await api.post(`/admin/kafka/consumer-groups/${resetForm.groupId}/reset-offsets`, {
        topic: resetForm.topic, to_earliest: resetForm.to_earliest
      });
      showToast('success', `Offsets reset for '${resetForm.groupId}'`);
      setShowResetOffsets(false);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to reset offsets');
    } finally {
      setActionLoading(prev => ({ ...prev, resetOffsets: false }));
    }
  };

  // Helpers
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'running': case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'stopped': case 'error': case 'critical': case 'exited': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'running': case 'healthy': return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'warning': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />;
      case 'stopped': case 'error': case 'critical': return <XCircleIcon className="h-5 w-5 text-red-600" />;
      default: return <ServerIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getLagColor = (lag) => {
    if (lag > 100) return 'text-red-600 bg-red-100';
    if (lag > 10) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: ChartBarIcon },
    { id: 'topics', label: 'Topics', icon: QueueListIcon },
    { id: 'consumers', label: 'Consumer Groups', icon: UsersIcon },
    { id: 'services', label: 'Services', icon: Cog6ToothIcon },
  ];

  // Modal component
  const Modal = ({ show, onClose, title, children, wide }) => {
    if (!show) return null;
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
          <div className={`relative bg-white rounded-xl shadow-2xl ${wide ? 'max-w-4xl' : 'max-w-lg'} w-full p-6 z-10`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
            </div>
            {children}
          </div>
        </div>
      </div>
    );
  };

  if (loading && !kafkaStatus) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toast */}
        {toast && (
          <div className={`fixed top-4 right-4 z-50 rounded-lg p-4 shadow-lg border max-w-md ${
            toast.type === 'success' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-red-50 border-red-300 text-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {toast.type === 'success' ? <CheckCircleIcon className="h-5 w-5" /> : <XCircleIcon className="h-5 w-5" />}
              <span className="text-sm font-medium">{toast.text}</span>
              <button onClick={() => setToast(null)} className="ml-2 text-xs opacity-60 hover:opacity-100">&times;</button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Kafka Management</h1>
              <p className="mt-1 text-sm text-gray-600">Monitor, manage topics, consumer groups, and services</p>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={() => setShowProduceModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium">
                <PaperAirplaneIcon className="h-4 w-4" /> Produce Message
              </button>
              <button onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${autoRefresh ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
                {autoRefresh ? 'Auto: ON' : 'Auto: OFF'}
              </button>
              <button onClick={() => { fetchKafkaStatus(); fetchClusterInfo(); fetchServiceStatus(); }} disabled={loading}
                className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                <ArrowPathIcon className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>
          {lastUpdated && <p className="mt-1 text-xs text-gray-400">Last updated: {lastUpdated.toLocaleTimeString()}</p>}
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-1">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}>
                <tab.icon className="h-4 w-4" /> {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Error State */}
        {error && !kafkaStatus && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <XCircleIcon className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Kafka is Not Running</h3>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => handleServiceControl('zookeeper', 'start')} disabled={actionLoading['zookeeper-start']}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 text-sm font-medium">
                {actionLoading['zookeeper-start'] ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlayIcon className="h-4 w-4" />}
                Start Zookeeper
              </button>
              <button onClick={() => handleServiceControl('kafka', 'start')} disabled={actionLoading['kafka-start']}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 text-sm font-medium">
                {actionLoading['kafka-start'] ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlayIcon className="h-4 w-4" />}
                Start Kafka
              </button>
              <button onClick={fetchKafkaStatus} disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
                <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Retry
              </button>
            </div>
          </div>
        )}

        {/* ==================== DASHBOARD TAB ==================== */}
        {activeTab === 'dashboard' && kafkaStatus && (
          <>
            {/* Broker Status */}
            <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Broker Status</h2>
                <div className="flex items-center gap-2">
                  {kafkaStatus.is_running ? (
                    <>
                      <button onClick={() => handleServiceControl('kafka', 'restart')} disabled={actionLoading['kafka-restart']}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-gray-300 text-xs font-medium">
                        {actionLoading['kafka-restart'] ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <ArrowPathIcon className="h-3.5 w-3.5" />} Restart
                      </button>
                      <button onClick={() => handleServiceControl('kafka', 'stop')} disabled={actionLoading['kafka-stop']}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 text-xs font-medium">
                        {actionLoading['kafka-stop'] ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <StopIcon className="h-3.5 w-3.5" />} Stop
                      </button>
                    </>
                  ) : (
                    <button onClick={() => handleServiceControl('kafka', 'start')} disabled={actionLoading['kafka-start']}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 text-xs font-medium">
                      {actionLoading['kafka-start'] ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />} Start
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-full ${getStatusColor(kafkaStatus.broker_status?.status)}`}>
                    {getStatusIcon(kafkaStatus.broker_status?.status)}
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{kafkaStatus.broker_status?.status?.toUpperCase() || 'UNKNOWN'}</p>
                    <p className="text-sm text-gray-600">{kafkaStatus.broker_status?.host}:{kafkaStatus.broker_status?.port}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Latency</p>
                  <p className="text-xl font-semibold text-gray-900">{kafkaStatus.broker_status?.latency_ms?.toFixed(1) || '—'} ms</p>
                </div>
              </div>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              {[
                { label: 'Brokers', value: kafkaStatus.metrics?.total_brokers, icon: ServerIcon, color: 'blue' },
                { label: 'Topics', value: kafkaStatus.metrics?.total_topics, icon: QueueListIcon, color: 'purple' },
                { label: 'Partitions', value: kafkaStatus.metrics?.total_partitions, icon: ChartBarIcon, color: 'indigo' },
                { label: 'Consumers', value: kafkaStatus.metrics?.active_consumers, icon: UsersIcon, color: 'green' },
                { label: 'Total Lag', value: kafkaStatus.metrics?.total_lag, icon: ExclamationTriangleIcon, color: 'yellow' },
              ].map((m, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">{m.label}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{m.value ?? 0}</p>
                    </div>
                    <div className={`p-2.5 bg-${m.color}-100 rounded-lg`}>
                      <m.icon className={`h-5 w-5 text-${m.color}-600`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Cluster Info */}
            {clusterInfo && (
              <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Cluster Information</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><p className="text-xs text-gray-500">Version</p><p className="text-sm font-medium">{clusterInfo.kafka_version}</p></div>
                  <div><p className="text-xs text-gray-500">Controller</p><p className="text-sm font-medium">Broker #{clusterInfo.controller}</p></div>
                  <div><p className="text-xs text-gray-500">Brokers</p>
                    {clusterInfo.brokers?.map((b, i) => <p key={i} className="text-sm font-medium">{b.host}:{b.port} (ID: {b.id})</p>)}
                  </div>
                  <div><p className="text-xs text-gray-500">Consumer Groups</p><p className="text-sm font-medium">{clusterInfo.consumer_groups}</p></div>
                </div>
              </div>
            )}

            {/* Quick Topics & Consumer Groups */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Topics Summary */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-base font-semibold text-gray-900">Topics ({kafkaStatus.topics?.length || 0})</h2>
                  <button onClick={() => setActiveTab('topics')} className="text-xs text-blue-600 hover:text-blue-800 font-medium">View All</button>
                </div>
                <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {kafkaStatus.topics?.slice(0, 8).map((topic, i) => (
                    <div key={i} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{topic.name}</p>
                        <p className="text-xs text-gray-500">{topic.partitions} partitions, {topic.replicas} replicas</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(topic.status)}`}>{topic.status}</span>
                    </div>
                  )) || <div className="px-6 py-4 text-sm text-gray-500 text-center">No topics</div>}
                </div>
              </div>

              {/* Consumer Groups Summary */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-base font-semibold text-gray-900">Consumer Groups ({kafkaStatus.consumer_groups?.length || 0})</h2>
                  <button onClick={() => setActiveTab('consumers')} className="text-xs text-blue-600 hover:text-blue-800 font-medium">View All</button>
                </div>
                <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {kafkaStatus.consumer_groups?.map((group, i) => (
                    <div key={i} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{group.group}</p>
                        <p className="text-xs text-gray-500">{new Date(group.last_activity).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLagColor(group.lag)}`}>Lag: {group.lag}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(group.status)}`}>{group.status}</span>
                      </div>
                    </div>
                  )) || <div className="px-6 py-4 text-sm text-gray-500 text-center">No consumer groups</div>}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ==================== TOPICS TAB ==================== */}
        {activeTab === 'topics' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Topics Management</h2>
              <button onClick={() => setShowCreateTopic(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                <PlusIcon className="h-4 w-4" /> Create Topic
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topic Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partitions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Replicas</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {kafkaStatus?.topics?.map((topic, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{topic.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{topic.partitions}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{topic.replicas}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(topic.status)}`}>{topic.status}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleViewTopicDetail(topic.name)} title="View Details"
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md">
                            <InformationCircleIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleViewMessages(topic.name)} title="Browse Messages"
                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md">
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => { setProduceForm({ ...produceForm, topic: topic.name }); setShowProduceModal(true); }} title="Produce Message"
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md">
                            <PaperAirplaneIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => setConfirmDelete({ type: 'purge', name: topic.name })} title="Purge Messages"
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-md">
                            <ArrowsPointingInIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => setConfirmDelete({ type: 'topic', name: topic.name })} title="Delete Topic"
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) || (
                    <tr><td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">No topics found. Kafka may not be running.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ==================== CONSUMER GROUPS TAB ==================== */}
        {activeTab === 'consumers' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Consumer Groups</h2>
              <button onClick={() => setShowResetOffsets(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium">
                <ArrowPathIcon className="h-4 w-4" /> Reset Offsets
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lag</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Activity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {kafkaStatus?.consumer_groups?.map((group, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{group.group}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getLagColor(group.lag)}`}>{group.lag}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{new Date(group.last_activity).toLocaleString()}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(group.status)}`}>{group.status}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleViewGroupDetail(group.group)} title="View Details"
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md">
                            <InformationCircleIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => { setResetForm({ groupId: group.group, topic: '', to_earliest: true }); setShowResetOffsets(true); }} title="Reset Offsets"
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-md">
                            <ArrowPathIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => setConfirmDelete({ type: 'group', name: group.group })} title="Delete Group"
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) || (
                    <tr><td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">No consumer groups found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ==================== SERVICES TAB ==================== */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            {/* Docker Services */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Docker Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['zookeeper', 'kafka', 'kafka-ui'].map(svc => {
                  const info = serviceStatus?.[svc];
                  const isRunning = info?.status === 'running';
                  return (
                    <div key={svc} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${isRunning ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          <h3 className="text-sm font-semibold text-gray-900 capitalize">{svc}</h3>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(info?.status)}`}>
                          {info?.status || 'unknown'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">Container: {info?.container || 'N/A'}</p>
                      {info?.started_at && info.started_at !== '' && (
                        <p className="text-xs text-gray-400 mb-3">Started: {new Date(info.started_at).toLocaleString()}</p>
                      )}
                      <div className="flex gap-2">
                        {isRunning ? (
                          <>
                            <button onClick={() => handleServiceControl(svc, 'restart')} disabled={actionLoading[`${svc}-restart`]}
                              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-md hover:bg-amber-100 text-xs font-medium">
                              {actionLoading[`${svc}-restart`] ? <ArrowPathIcon className="h-3 w-3 animate-spin" /> : <ArrowPathIcon className="h-3 w-3" />} Restart
                            </button>
                            <button onClick={() => handleServiceControl(svc, 'stop')} disabled={actionLoading[`${svc}-stop`]}
                              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100 text-xs font-medium">
                              {actionLoading[`${svc}-stop`] ? <ArrowPathIcon className="h-3 w-3 animate-spin" /> : <StopIcon className="h-3 w-3" />} Stop
                            </button>
                          </>
                        ) : (
                          <button onClick={() => handleServiceControl(svc, 'start')} disabled={actionLoading[`${svc}-start`]}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 text-xs font-medium">
                            {actionLoading[`${svc}-start`] ? <ArrowPathIcon className="h-3 w-3 animate-spin" /> : <PlayIcon className="h-3 w-3" />} Start
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <button onClick={() => { handleServiceControl('zookeeper', 'start'); setTimeout(() => handleServiceControl('kafka', 'start'), 5000); setTimeout(() => handleServiceControl('kafka-ui', 'start'), 8000); }}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 text-left">
                  <PlayIcon className="h-6 w-6 text-green-600 mb-2" />
                  <p className="text-sm font-semibold text-gray-900">Start All</p>
                  <p className="text-xs text-gray-500">Start Zookeeper, Kafka, and UI</p>
                </button>
                <button onClick={() => { handleServiceControl('kafka-ui', 'stop'); handleServiceControl('kafka', 'stop'); setTimeout(() => handleServiceControl('zookeeper', 'stop'), 3000); }}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-300 text-left">
                  <StopIcon className="h-6 w-6 text-red-600 mb-2" />
                  <p className="text-sm font-semibold text-gray-900">Stop All</p>
                  <p className="text-xs text-gray-500">Stop all Kafka services</p>
                </button>
                <button onClick={() => { handleServiceControl('zookeeper', 'restart'); setTimeout(() => handleServiceControl('kafka', 'restart'), 5000); }}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-amber-50 hover:border-amber-300 text-left">
                  <ArrowPathIcon className="h-6 w-6 text-amber-600 mb-2" />
                  <p className="text-sm font-semibold text-gray-900">Restart All</p>
                  <p className="text-xs text-gray-500">Restart entire Kafka stack</p>
                </button>
                <a href="http://localhost:8091" target="_blank" rel="noopener noreferrer"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 text-left block">
                  <ServerIcon className="h-6 w-6 text-blue-600 mb-2" />
                  <p className="text-sm font-semibold text-gray-900">Kafka UI</p>
                  <p className="text-xs text-gray-500">Open Kafka UI (port 8091)</p>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* ==================== MODALS ==================== */}

        {/* Create Topic Modal */}
        <Modal show={showCreateTopic} onClose={() => setShowCreateTopic(false)} title="Create New Topic">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Topic Name</label>
              <input type="text" value={newTopic.name} onChange={e => setNewTopic({ ...newTopic, name: e.target.value })}
                placeholder="e.g. sms.requests" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Partitions</label>
                <input type="number" min="1" max="100" value={newTopic.partitions} onChange={e => setNewTopic({ ...newTopic, partitions: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Replication Factor</label>
                <input type="number" min="1" max="3" value={newTopic.replication_factor} onChange={e => setNewTopic({ ...newTopic, replication_factor: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowCreateTopic(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleCreateTopic} disabled={actionLoading.createTopic}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300">
                {actionLoading.createTopic ? 'Creating...' : 'Create Topic'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Topic Detail Modal */}
        <Modal show={showTopicDetail} onClose={() => setShowTopicDetail(false)} title={`Topic: ${topicDetail?.name}`} wide>
          {topicDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Total Messages</p>
                  <p className="text-lg font-bold text-gray-900">{topicDetail.total_messages?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Partitions</p>
                  <p className="text-lg font-bold text-gray-900">{topicDetail.partitions}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Replicas</p>
                  <p className="text-lg font-bold text-gray-900">{topicDetail.replicas}</p>
                </div>
              </div>
              {topicDetail.partition_details && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Partition Details</h4>
                  <div className="overflow-x-auto max-h-64">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Partition</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Leader</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">First Offset</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Last Offset</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Messages</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ISR</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {topicDetail.partition_details.map((p, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 font-medium">{p.id}</td>
                            <td className="px-3 py-2">{p.leader}</td>
                            <td className="px-3 py-2">{p.first_offset}</td>
                            <td className="px-3 py-2">{p.last_offset}</td>
                            <td className="px-3 py-2 font-medium">{p.message_count?.toLocaleString()}</td>
                            <td className="px-3 py-2">{p.isr?.join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Messages Modal */}
        <Modal show={showMessages} onClose={() => setShowMessages(false)} title={`Messages: ${topicMessages?.topic}`} wide>
          {topicMessages && (
            <div>
              <p className="text-sm text-gray-500 mb-3">{topicMessages.count} messages retrieved</p>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {topicMessages.messages?.map((msg, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-700">Partition: {msg.partition} | Offset: {msg.offset}</span>
                      <span className="text-gray-400">{new Date(msg.time).toLocaleString()}</span>
                    </div>
                    {msg.key && <p className="text-gray-600 mb-1"><span className="font-medium">Key:</span> {msg.key}</p>}
                    <pre className="bg-white border border-gray-200 rounded p-2 overflow-x-auto text-xs text-gray-800 whitespace-pre-wrap break-all">{
                      (() => { try { return JSON.stringify(JSON.parse(msg.value), null, 2); } catch { return msg.value; } })()
                    }</pre>
                  </div>
                )) || <p className="text-sm text-gray-500 text-center py-4">No messages found</p>}
              </div>
            </div>
          )}
        </Modal>

        {/* Produce Message Modal */}
        <Modal show={showProduceModal} onClose={() => setShowProduceModal(false)} title="Produce Message">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
              <select value={produceForm.topic} onChange={e => setProduceForm({ ...produceForm, topic: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select topic...</option>
                {kafkaStatus?.topics?.map((t, i) => <option key={i} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Key (optional)</label>
              <input type="text" value={produceForm.key} onChange={e => setProduceForm({ ...produceForm, key: e.target.value })}
                placeholder="message-key" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Value (JSON or text)</label>
              <textarea rows={6} value={produceForm.value} onChange={e => setProduceForm({ ...produceForm, value: e.target.value })}
                placeholder='{"message": "Hello Kafka"}' className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowProduceModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleProduceMessage} disabled={actionLoading.produce}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300">
                {actionLoading.produce ? 'Sending...' : 'Produce'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Consumer Group Detail Modal */}
        <Modal show={showGroupDetail} onClose={() => setShowGroupDetail(false)} title={`Consumer Group: ${groupDetail?.group_id}`} wide>
          {groupDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">State</p>
                  <p className="text-sm font-bold text-gray-900">{groupDetail.state || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Members</p>
                  <p className="text-sm font-bold text-gray-900">{groupDetail.members}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Total Lag</p>
                  <p className="text-sm font-bold text-gray-900">{groupDetail.total_lag}</p>
                </div>
              </div>
              {groupDetail.topic_lags?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Topic Lags</h4>
                  <div className="space-y-1">
                    {groupDetail.topic_lags.map((tl, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 rounded p-2">
                        <span className="text-sm text-gray-700">{tl.topic} ({tl.partitions} partitions)</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLagColor(tl.lag)}`}>Lag: {tl.lag}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {groupDetail.member_info?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Members</h4>
                  <div className="space-y-1">
                    {groupDetail.member_info.map((m, i) => (
                      <div key={i} className="bg-gray-50 rounded p-2 text-xs">
                        <span className="font-medium">{m.client_id}</span> — {m.host}
                        <span className="text-gray-400 ml-2 text-[10px]">{m.member_id}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Reset Offsets Modal */}
        <Modal show={showResetOffsets} onClose={() => setShowResetOffsets(false)} title="Reset Consumer Group Offsets">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consumer Group</label>
              <select value={resetForm.groupId} onChange={e => setResetForm({ ...resetForm, groupId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select group...</option>
                {kafkaStatus?.consumer_groups?.map((g, i) => <option key={i} value={g.group}>{g.group}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
              <select value={resetForm.topic} onChange={e => setResetForm({ ...resetForm, topic: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select topic...</option>
                {kafkaStatus?.topics?.map((t, i) => <option key={i} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reset To</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={resetForm.to_earliest} onChange={() => setResetForm({ ...resetForm, to_earliest: true })} /> Earliest
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={!resetForm.to_earliest} onChange={() => setResetForm({ ...resetForm, to_earliest: false })} /> Latest
                </label>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">Warning: The consumer group must be stopped (no active consumers) before resetting offsets.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowResetOffsets(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleResetOffsets} disabled={actionLoading.resetOffsets}
                className="px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:bg-gray-300">
                {actionLoading.resetOffsets ? 'Resetting...' : 'Reset Offsets'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Confirm Delete Modal */}
        <Modal show={!!confirmDelete} onClose={() => setConfirmDelete(null)} title={confirmDelete?.type === 'purge' ? 'Purge Topic' : `Delete ${confirmDelete?.type === 'topic' ? 'Topic' : 'Consumer Group'}`}>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                {confirmDelete?.type === 'purge'
                  ? `This will delete ALL messages from topic "${confirmDelete?.name}" by recreating it. This cannot be undone.`
                  : `Are you sure you want to delete "${confirmDelete?.name}"? This action cannot be undone.`}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={() => {
                if (confirmDelete?.type === 'topic') handleDeleteTopic(confirmDelete.name);
                else if (confirmDelete?.type === 'purge') handlePurgeTopic(confirmDelete.name);
                else if (confirmDelete?.type === 'group') handleDeleteGroup(confirmDelete.name);
              }} className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700">
                {confirmDelete?.type === 'purge' ? 'Purge' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>

      </div>
    </div>
  );
};

export default KafkaStatus;
